import { useContext, useEffect, useMemo, useState } from "react";
import DatePicker from "react-datepicker";
import { FilterContext } from "../../context/FilterContext";
import { InstanceComparisonOptionsProps } from "./statistics-comparison/types";
import Filter from "../../types/Filter";
import { Dropdown } from "../../components/forms";
import { dateForPickerDisplay, dateFromPickerSelection, getParameter, getTimeZone } from "../../helpers/utils";
import * as dayjs from "dayjs";

const InstanceComparisonOptions = (props: InstanceComparisonOptionsProps) => {
    const instances = useMemo(() => props.instances, [props.instances]);
    const { filters } = useContext(FilterContext);
    const [instanceId, setInstanceId] = useState<number>(props.instanceId)
    const [instanceType, setInstanceType] = useState<string | undefined>(props.instanceType)
    const [instanceName, setInstanceName] = useState<string>(props.instanceName)
    const [snapshotId, setSnapshotId] = useState<number | undefined>(props.snapshotId)
    const [periodStart, setPeriodStart] = useState<Date | null>(props.periodStart)
    const [periodEnd, setPeriodEnd] = useState<Date | null>(props.periodEnd)

    useEffect(() => {
        if (instanceId && !instanceType) {
            setTimeout(() => {
                setInstanceType(instances.find(i => i.id === instanceId)?.type);
                setInstanceName(instances.find(i => i.id === instanceId)?.name || '');
            }, 0)
        }
    }, []);

    useEffect(() => {
        setPeriodStart(props.periodStart)
    }, [props.periodStart])

    useEffect(() => {
        setPeriodEnd(props.periodEnd)
    }, [props.periodEnd])

    useEffect(() => {
        if (!props.snapshotId && snapshotId !== props.snapshotId) {
            setSnapshotId(props.snapshotId)
        }
    }, [props.snapshotId])

    useEffect(() => {
        props.onOptionsChange({ instanceId, instanceType, snapshotId, periodStart, periodEnd, instanceName })
    }, [instanceId, snapshotId, periodEnd, periodStart, instanceType, instanceName])

    useEffect(() => {
        if (props.instanceId !== -1) {
            setInstanceId(props.instanceId)
            setInstanceType(props.instanceType)
            setInstanceName(props.instanceName)
            setSnapshotId(props.snapshotId)
        }
        else {
            // reset values
            setInstanceId(-1)
            setInstanceType(undefined)
            setInstanceName('')
            setSnapshotId(NaN)
        }
    }, [props.instanceId])

    const getDateFromSnapshot = (parameters: string) => {
        const from: string | null = getParameter(parameters, 'from');
        const to: string | null = getParameter(parameters, 'to');
        const tz: string | null = getParameter(parameters, 'tz');
        const fromDate = from ? (tz ? dayjs.tz(from, tz) : dayjs.default(from)) : dayjs.default();
        const toDate = to ? (tz ? dayjs.tz(to, tz) : dayjs.default(to)) : dayjs.default();
        return { from: fromDate.toDate(), to: toDate.toDate() }
    }

    function applySnapShot(snapshot: Filter) {
        const parameters = snapshot.parameters
        const dateFromSnapshot = getDateFromSnapshot(parameters)
        setPeriodStart(dateFromSnapshot.from)
        setPeriodEnd(dateFromSnapshot.to)
        setSnapshotId(snapshot.id)
    }

    const onInstanceSelect = (id: number) => {
        setInstanceId(id);
        setInstanceType(instances.find(i => i.id === id)?.type);
        setInstanceName(instances.find(i => i.id === id)?.name || '');
    };

    const timezone = getTimeZone();

    return <div className="row row-cols-lg-12 mt-3 comparison-filter" key={props.instanceId}>
        <div className='col-xs-34 col-md-3 col-lg-3 comparison-filter-instance'>
            <span className="required">Instance</span><br/>
            <Dropdown
                value={instanceId > 0 ? instanceId : ''}
                onChange={(value) => onInstanceSelect(Number(value))}
                options={instances.map(instance => ({ value: instance.id, label: instance.name }))}
                placeholder="Select instance"
                filter
                filterPlaceholder="Search instances"
                ariaLabel="Instance"
            />
        </div>
        <div className='col-xs-3 col-md-3 col-lg-3'>
            <span className="required"><label htmlFor="period-start" className="reminder"
                                              data-tooltip-id="global-tooltip" data-tooltip-content="The start time to compare against">Period Start</label></span><br/>
            <DatePicker
                    name="period-start"
                    className={`form-control w-100`}
                    dateFormat="yyyy/MM/dd HH:mm"
                    placeholderText="yyyy/MM/dd"
                    disabled={props.loading}
                    popperPlacement="auto"
                    showTimeSelect
                    timeFormat="HH:mm"
                    maxDate={null}
                    onChange={(date: Date | null) => {
                        setSnapshotId(NaN)
                        setPeriodStart(dateFromPickerSelection(date, timezone))
                    }
                    }
                    selected={dateForPickerDisplay(periodStart, timezone)}
                    shouldCloseOnSelect={true}/>
        </div>
        <div className='col-xs-3 col-md-3 col-lg-3'>
            <span className="required"><label htmlFor="period-end" className="reminder"
                                              data-tooltip-id="global-tooltip" data-tooltip-content="The end time to compare against">Period End</label></span><br/>
            <DatePicker
                    name="period-end"
                    className={`form-control w-100`}
                    dateFormat="yyyy/MM/dd HH:mm"
                    placeholderText="yyyy/MM/dd"
                    popperPlacement="auto"
                    disabled={props.loading}
                    showTimeSelect
                    timeFormat="HH:mm"
                    maxDate={null}
                    onChange={(date: Date | null) => {
                        setPeriodEnd(dateFromPickerSelection(date, timezone))
                        setSnapshotId(NaN)
                    }
                    }
                    selected={dateForPickerDisplay(periodEnd, timezone)}
                    shouldCloseOnSelect={true}/>
        </div>


        <div className="col-xs-3 col-md-3 col-lg-3">
            <label>Snapshot</label><br/>
            <Dropdown
                value={snapshotId || ''}
                onChange={(value) => {
                    const snapshot = filters.find(item => item.id === Number(value))
                    if (snapshot) applySnapShot(snapshot)
                    else setSnapshotId(NaN)
                }}
                options={filters.map(item => ({ value: item.id, label: item.name }))}
                placeholder="Choose snapshot"
                filter
                filterPlaceholder="Search snapshots"
                showClear
                ariaLabel={`Snapshot for ${props.instanceKey === 'firstInstanceOptions' ? 'first' : 'second'} instance`}
            />
        </div>

    </div>
}

export default InstanceComparisonOptions;
